import Fuse from 'fuse.js';

import { trimTextToEmpty } from 'flow/utils/message-utils';
import { makeBold } from 'flow/utils/element-utils';

const FIELDS_DISPLAY_FULL = [
    'email',
    'fixedNumber',
    'mobileNumber',
    'altTelephone1',
    'altTelephone2',
];

/**
 * Use this to make a fuzzy search on the searchables argument.
 *
 * @param searchables is expected to be an immutable set
 * @param searchText is input string from a text input by a user
 * @param searchablesKeyMapper is required to map out what values the search should look for
 * If an entry with the name 'key' is supplied, it will not search for this value,
 * but good for connecting the search results to specific keys/items.
 * @param keys used for determining which fields should be searchable
 * @param options optional parameter for customizing fuzzy options
 *
 * Returns a JS object as supplied by the Fuse library and is affected by the options.
 */
export const fuzzySearch = (
    searchables,
    searchText,
    searchablesKeyMapper,
    keys,
    options,
) => {
    if (!searchables) return [];
    return searchFuseInstance(
        createFuseInstance(searchables, searchablesKeyMapper, options, keys),
        searchText,
    );
};

export const searchFuseInstance = (fuseInstance, searchText) => {
    if (!fuseInstance) return [];
    return fuzzySearchMatchAllWords(fuseInstance, trimTextToEmpty(searchText));
};

const DEFAULT_FUZZY_OPTIONS = {
    ignoreLocation: true,
    shouldSort: false,
    threshold: 0.3,
};

const attachKeysToFuseOptions = (options, keys) => {
    return {
        ...options,
        keys,
    };
};

export const createFuseInstance = (searchables, keyMapper, options, keys) => {
    if (!searchables || searchables.isEmpty()) return null;

    if (!options) {
        options = DEFAULT_FUZZY_OPTIONS;
    }

    const fuseReadySearchables = searchables
        .map(keyMapper)
        .valueSeq()
        .toArray()
        .filter(Boolean);

    const fuseReadyOptions = attachKeysToFuseOptions(options, keys);
    return new Fuse(fuseReadySearchables, fuseReadyOptions);
};

const fuzzySearchMatchAllWords = (fuse, filterText) => {
    if (fuse.options.useExtendedSearch === true) {
        const searchParts = filterText
            .split(' ')
            .filter((part) => part !== '')
            .map((part) => `'${part}`);

        return fuse.search({
            $and: searchParts.map((part) => ({
                $or: fuse.options.keys.map((k) => ({ [k.name]: part })),
            })),
        });
    }

    const searchWordsArray = filterText.split(' ');
    let result = [];
    searchWordsArray.forEach((word, index) => {
        const fuzzyResult = fuse.search(word);
        if (index === 0) {
            result = fuzzyResult;
        } else {
            const fuzzyResultKeys = fuzzyResult.map((r) => r.item.key);
            result = result.filter((o) => fuzzyResultKeys.includes(o.item.key));
            result.forEach((fuzzyObject) => {
                // Extract matches and add to results for highlighting
                const itemWithNewData = fuzzyResult.find(
                    (f) => f.item.key === fuzzyObject.item.key,
                );
                if (fuse.options.includeMatches) {
                    fuzzyObject.matches.push(...itemWithNewData.matches);
                }
                fuzzyObject.score = fuzzyObject.score + itemWithNewData.score;
            });
        }
    });

    // If we have more than one word we try to find a match where one field includes the
    // entire search string. If we do, we replace the prevoius match with that and
    // prioritize it by subtracting 1 from its score.
    if (searchWordsArray.length > 1) {
        const includesMatch = fuse.search(`'"${filterText}"`);
        const includesMatchKeys = includesMatch.map((o) => o.item.key);
        result
            .filter((o) => includesMatchKeys.includes(o.item.key))
            .forEach((fuzzyObject) => {
                const newItem = includesMatch.find(
                    (f) => f.item.key === fuzzyObject.item.key,
                );
                if (fuse.options.includeMatches) {
                    fuzzyObject.matches.push(...newItem.matches);
                }
                fuzzyObject.score = newItem.score - 1;
            });
    }

    return result;
};

/**
 * Will return an array with the match results that are ready to be rendered by the dom.
 * [nameResult, otherResult]
 * Will never return values on both variables.
 * If there is a match on the name, we don't want to display other matches.
 */
export const getParsedFuzzyTexts = (matches, i18next, translationRoot) => {
    if (!matches) return [];

    let nameResult;
    let otherResult;

    const nameMatches = matches.filter((match) => match['key'] === 'name');
    nameMatches.forEach((match) => {
        if (!nameResult) {
            nameResult = {};
            nameResult[match.key] = { value: match.value, indices: [] };
        }
        nameResult[match.key].indices.push(...match.indices);
    });

    if (!nameResult) {
        const otherMatches = matches.filter((match) => match['key'] !== 'name');
        otherMatches.forEach((match) => {
            if (!otherResult) otherResult = {};
            if (!otherResult[match.key])
                otherResult[match.key] = { value: match.value, indices: [] };

            otherResult[match.key].indices.push(...match.indices);
        });
    }

    return [
        getFuzzyParsedName(nameResult),
        getFuzzyParsedOther(otherResult, i18next, translationRoot),
    ];
};

/**
 * Returns an array that is ready to be rendered by the dom
 * if there are any matches on the name
 */
export const getFuzzyParsedName = (nameResults) => {
    if (!nameResults || !nameResults.name) return null;
    if (nameResults.name.indices.length === 0) return nameResults.name;

    const name = nameResults.name.value;

    return makeBold(name, nameResults.name.indices);
};

/**
 * Returns an array that is ready to be rendered by the dom
 */
export const getFuzzyParsedOther = (otherResults, i18next, translationRoot) => {
    if (!otherResults) return null;

    const matchesBolded = Object.keys(otherResults).map((key) => {
        const value = otherResults[key].value;
        const bolded = makeBold(value, otherResults[key].indices);
        if (!FIELDS_DISPLAY_FULL.includes(key) && value.length > 15) {
            const firstIndexCut = Math.max(bolded[0].length - 4, 0);
            bolded[0] = bolded[0].slice(firstIndexCut);
            bolded[bolded.length - 1] = bolded[bolded.length - 1].slice(0, 4);
        }
        return {
            key: key,
            text: bolded,
        };
    });

    return Object.values(matchesBolded).reduce((acc, match, index) => {
        if (index !== 0) {
            acc.push(', ');
        }

        const translationKey = `${translationRoot}.${match.key}`;

        acc.push([`${i18next.t(translationKey, match.key)}: `, ...match.text]);
        return acc;
    }, []);
};
